import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import type { SubTab } from './navbar-types';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';

interface NavbarProfileProps {
  subtabs: SubTab[];
}

const handleLinkClick = (
  e: React.MouseEvent,
  href: string,
  internal: boolean | undefined,
  navigate: (path: string) => void,
) => {
  if (href === '#') e.preventDefault();
  else if (internal) {
    e.preventDefault();
    navigate(href);
  }
};

export const NavbarProfile = ({ subtabs }: NavbarProfileProps) => {
  const router = useRouter();
  const navigate = (path: string) => router.push(path);
  const { logout } = useAuth();
  const { firstName, lastName } = useProfile();
  const [isHovered, setIsHovered] = useState(false);
  const [forceClosed, setForceClosed] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const displayName = `${firstName ?? ''} ${lastName ?? ''}`.trim();
  const isOpen = isHovered && !forceClosed;

  const handleMouseEnter = () => setIsHovered(true);

  const handleMouseLeave = () => {
    setIsHovered(false);
    setForceClosed(false);
  };

  const handleClick = () => {
    if (isOpen) {
      setForceClosed(true);
    } else {
      setForceClosed(false);
      setIsHovered(true);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsHovered(false);
        setForceClosed(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      className="navbar-profile-wrapper"
      ref={wrapperRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="navbar-tab" onClick={handleClick} aria-expanded={isOpen}>
        <span>{displayName}</span>
        <i className="fa fa-angle-down navbar-tab-icon" aria-hidden />
      </div>
      <div
        className={`navbar-dropdown navbar-dropdown-profile${isOpen ? ' navbar-dropdown--open' : ''}`}
      >
        {subtabs.map((subtab) => (
          <a
            key={subtab.label}
            href={subtab.href}
            className="navbar-subtab"
            onClick={(e) => {
              handleLinkClick(e, subtab.href, subtab.internal, navigate);
              setIsHovered(false);
              setForceClosed(false);
            }}
          >
            {subtab.label}
          </a>
        ))}
        <hr className="navbar-dropdown-divider" />
        <a
          href="#"
          className="navbar-subtab"
          onClick={(e) => {
            e.preventDefault();
            logout();
            setIsHovered(false);
            setForceClosed(false);
          }}
        >
          Log Out
        </a>
      </div>
    </div>
  );
};
